// InfiniteCarousel.jsx (Updated for dynamic looping)
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { icons } from '../..';

const CarouselWrapper = styled.div`
  width: 100%;
  max-width: 600px;
  overflow: hidden;
  position: relative;
  margin: auto;
`;

const CarouselTrack = styled.div`
  display: flex;
  width: ${({ totalCount, visibleCount }) => `${(totalCount * 100) / visibleCount}%`};
  transition: ${({ animate }) => (animate ? 'transform 0.5s ease' : 'none')};
  transform: ${({ index, visibleCount }) => `translateX(-${(index * 100) / visibleCount}%)`};
  cursor: grab;
  user-select: none;
  padding-left:30px;
  padding-right:30px;
`;

const GradientOverlayLeft = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 20px;
  height: 100%;
  z-index: 2;
  background: linear-gradient(to right, white 0%, transparent 100%);
  pointer-events: none;
`;

const GradientOverlayRight = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  width: 20px;
  height: 100%;
  z-index: 2;
  background: linear-gradient(to left, white 0%, transparent 100%);
  pointer-events: none;
`;

const CarouselItem = styled.div`
  flex: 0 0 calc((100%) / 3);
  padding: 10px;
  box-sizing: border-box;
`;

const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0,0,0,0.5);
  color: white;
  border: none;
  font-size: 18px;
  padding: 10px;
  z-index: 5;
  cursor: pointer;
  transition: all 0.3s;
  height:30px;
  width:30px;
  display:flex;
  justify-content:center;
  align-items:center;
  align-content:center;
  border-radius:100px;

  &:hover {
    background: #000;
  }
`;

const PrevButton = styled(NavButton)`left: 0;`;
const NextButton = styled(NavButton)`right: 0;`;

const InfiniteCarousel = ({ items }) => {
  const [index, setIndex] = useState(3); // initial index (first visible item set)
  const [animate, setAnimate] = useState(true);
  const startX = useRef(0);
  const isDragging = useRef(false);
  const deltaX = useRef(0);

  // Duplicate items for seamless looping
  const cloneCount = items.length;
  const extendedItems = [
    ...items.slice(-cloneCount),
    ...items,
    ...items.slice(0, cloneCount),
  ];

  const goTo = (newIndex) => {
    setIndex(newIndex);
    setAnimate(true);
  };

  const next = () => goTo(index + 1);
  const prev = () => goTo(index - 1);

  useEffect(() => {
    if (index === extendedItems.length - cloneCount) {
      setTimeout(() => {
        setAnimate(false);
        setIndex(cloneCount);
      }, 500);
    } else if (index === 0) {
      setTimeout(() => {
        setAnimate(false);
        setIndex(extendedItems.length - (cloneCount * 2));
      }, 500);
    }
  }, [index, extendedItems.length]);

  const onStart = (x) => {
    startX.current = x;
    isDragging.current = true;
    deltaX.current = 0;
  };

  const onMove = (x) => {
    if (!isDragging.current) return;
    deltaX.current = x - startX.current;
  };

  const onEnd = () => {
    if (!isDragging.current) return;

    if (deltaX.current > 50) prev();
    else if (deltaX.current < -50) next();

    isDragging.current = false;
    deltaX.current = 0;
  };

  return (
    <CarouselWrapper>
      <PrevButton onClick={prev}><icons.ChevronLeftStyledSmallWhite /></PrevButton>
      <NextButton onClick={next}><icons.ChevronRightStyledSmallWhite /></NextButton>
        <GradientOverlayLeft />
        <GradientOverlayRight />
        
      <CarouselTrack
        index={index}
        animate={animate}
        itemCount={extendedItems.length}
        visibleCount={3}
        onMouseDown={(e) => onStart(e.clientX)}
        onMouseMove={(e) => onMove(e.clientX)}
        onMouseUp={onEnd}
        onMouseLeave={onEnd}
        onTouchStart={(e) => onStart(e.touches[0].clientX)}
        onTouchMove={(e) => onMove(e.touches[0].clientX)}
        onTouchEnd={onEnd}
      >
        {extendedItems.map((item, i) => (
          <CarouselItem itemCount={extendedItems.length} key={i}>{item}</CarouselItem>
        ))}
      </CarouselTrack>
    </CarouselWrapper>
  );
};

export default InfiniteCarousel;
